import { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  FaWhatsapp,
  FaMapMarkerAlt,
  FaBed,
  FaBath,
  FaRulerCombined,
  FaCar,
  FaInfoCircle,
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import { FreeMode, Navigation, Thumbs, Autoplay, EffectFade } from "swiper";
import FSLightbox from "fslightbox-react";
import { Helmet } from "react-helmet";
import { HouseContext } from "../../context/HouseContext";
import { BounceLoader } from "react-spinners";
import Map from "../../components/Map";
import ScrollToTop from "../../components/ScrollToTop";

const PropertyDetails = () => {
  const { houses } = useContext(HouseContext);
  const { id } = useParams();
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [property, setProperty] = useState(null);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    setProperty(foundProperty);
  }, [id, houses]);

  if (!property) {
    return (
      <div className="flex flex-col justify-center items-center h-[90vh]">
        <BounceLoader className="my-2" color="#F79330" size={60} />
        <h2 className="text-xl">Cargando Propiedad</h2>
      </div>
    );
  }

  const openLightbox = (index) => {
    setPhotoIndex(index + 1);
    setLightboxToggler(!lightboxToggler);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const isFormComplete = () => {
    return (
      formData.name && formData.email && formData.phone && formData.message
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const whatsappMessage = encodeURIComponent(`\
      *Consulta sobre la propiedad* \n
      *Nombre*: ${formData.name} \n
      *Email*: ${formData.email} \n
      *Teléfono*: ${formData.phone} \n
      *Mensaje*: ${formData.message} \n\n
      *Detalles de la propiedad* \n
      *Nombre*: ${property.name} \n
      *Dirección*: ${property.address} \n
      *Localidad*: ${property.country} \n
      *Tipo*: ${property.type} \n
      *Precio*: ${property.currency} ${
      property.price !== "" ? property.price : "Consultar"
    } \n
      *Descripción*: ${property.description}
    `);

    const whatsappLink = `https://wa.me/+5492241492969?text=${whatsappMessage}`;
    window.open(whatsappLink, "_blank");
  };

  // Función para formatear características
  const formatCharacteristic = (key) => {
    const translations = {
      bedrooms: "Dormitorios",
      bathrooms: "Baños",
      surface: "Superficie",
      garage: "Cochera",
      floors: "Pisos",
      year: "Año construcción",
      // Agrega más traducciones según necesites
    };

    return translations[key] || key;
  };

  return (
    <div className="max-w-7xl mx-auto min-h-[800px] mt-20 mb-14 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>{property.name}</title>
        <meta
          name="description"
          content={`Consulta sobre ${property.name}. ${property.description}`}
        />
        <meta
          name="keywords"
          content={`${property.name}, ${property.type}, ${property.country}, propiedades, alquiler`}
        />
      </Helmet>
      <ScrollToTop />

      {/* Encabezado */}
      <div className="py-8">
        <div className="bg-white p-6 rounded-xl shadow-md flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {property.name}
            </h1>
            <div className="flex items-center mt-2 text-gray-600">
              <FaMapMarkerAlt className="mr-1" />
              <span>
                {property.address}, {property.country}
              </span>
            </div>
          </div>
          <div className="mt-4 md:mt-0">
            <div className="bg-primary/10 px-6 py-3 rounded-lg">
              <p className="text-lg font-medium text-gray-600">
                {property.state}
              </p>
              <p className="text-3xl font-bold text-primary">
                {property.currency !== "" ? (
                  <>
                    {property.currency} {property.price}
                  </>
                ) : (
                  "Consultar"
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Características principales */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {property.characteristics.bedrooms && (
            <div className="flex items-center">
              <FaBed className="text-primary mr-2 text-xl" />
              <div>
                <p className="text-gray-500 text-sm">Dormitorios</p>
                <p className="font-semibold">
                  {property.characteristics.bedrooms}
                </p>
              </div>
            </div>
          )}
          {property.characteristics.bathrooms && (
            <div className="flex items-center">
              <FaBath className="text-primary mr-2 text-xl" />
              <div>
                <p className="text-gray-500 text-sm">Baños</p>
                <p className="font-semibold">
                  {property.characteristics.bathrooms}
                </p>
              </div>
            </div>
          )}
          {property.characteristics.surface && (
            <div className="flex items-center">
              <FaRulerCombined className="text-primary mr-2 text-xl" />
              <div>
                <p className="text-gray-500 text-sm">Superficie</p>
                <p className="font-semibold">
                  {property.characteristics.surface} m²
                </p>
              </div>
            </div>
          )}
          {property.characteristics.garage && (
            <div className="flex items-center">
              <FaCar className="text-primary mr-2 text-xl" />
              <div>
                <p className="text-gray-500 text-sm">Cochera</p>
                <p className="font-semibold">
                  {property.characteristics.garage}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Galería de imágenes */}
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="mb-8 rounded-xl overflow-hidden shadow-md sm:w-10/12 w-full">
          <Swiper
            style={{
              "--swiper-navigation-color": "#fff",
              "--swiper-pagination-color": "#fff",
            }}
            loop={true}
            spaceBetween={10}
            navigation={true}
            thumbs={thumbsSwiper ? { swiper: thumbsSwiper } : undefined}
            modules={[FreeMode, Navigation, Thumbs, Autoplay, EffectFade]}
            className="mySwiper2"
            effect="fade"
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            centeredSlides={true}
            slidesPerView={1}
            grabCursor={true}
          >
            {property.images.map((image, index) => (
              <SwiperSlide key={index}>
                <div className="relative h-96 sm:h-[500px] w-full">
                  <img
                    src={image}
                    alt={`Room ${index}`}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => openLightbox(index)}
                  />
                  <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {index + 1} / {property.images.length}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Miniaturas */}
          <Swiper
            onSwiper={setThumbsSwiper}
            loop={true}
            spaceBetween={8}
            slidesPerView={4}
            freeMode={true}
            watchSlidesProgress={true}
            modules={[FreeMode, Navigation, Thumbs]}
            className="mySwiper p-4 bg-gray-50"
            breakpoints={{
              640: {
                slidesPerView: 5,
              },
              768: {
                slidesPerView: 6,
              },
              1024: {
                slidesPerView: 7,
              },
            }}
          >
            {property.images.map((image, index) => (
              <SwiperSlide key={index}>
                <div className="relative h-20 cursor-pointer group">
                  <img
                    src={image}
                    alt={`Room ${index}`}
                    className="w-full h-full object-cover rounded-md border-2 border-transparent group-hover:border-primary transition-all"
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all" />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <FSLightbox
          toggler={lightboxToggler}
          sources={property.images}
          slide={photoIndex}
        />

        {/* Sección derecha (Formulario de contacto) */}
        <div className="lg:w-1/3">
          <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-4">
            <div className="bg-primary p-4">
              <h2 className="text-xl font-semibold text-white">
                Contactar a la inmobiliaria
              </h2>
            </div>
            <div className="p-6">
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nombre completo*
                  </label>
                  <input
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email*
                  </label>
                  <input
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                    type="email"
                    id="email"
                    name="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Teléfono*
                  </label>
                  <input
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition"
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+54 9 11 1234-5678"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Mensaje*
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition h-32"
                    id="message"
                    name="message"
                    placeholder="Escribe tu consulta aquí..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={!isFormComplete()}
                  className={`w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition ${
                    isFormComplete()
                      ? "bg-primary hover:bg-primary-dark"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FaWhatsapp className="mr-2 text-xl" />
                  Enviar por WhatsApp
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sección izquierda (Descripción, características, ubicación) */}
        <div className="lg:w-full">
          {/* Pestañas */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab("description")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "description"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Descripción
              </button>
              <button
                onClick={() => setActiveTab("features")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "features"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Características
              </button>
              <button
                onClick={() => setActiveTab("location")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "location"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Ubicación
              </button>
            </nav>
          </div>

          {/* Contenido de pestañas */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {activeTab === "description" && (
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <FaInfoCircle className="text-primary mr-2" />
                  Sobre la propiedad
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {property.description}
                </p>
              </div>
            )}

            {activeTab === "features" && (
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Detalles</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(property.characteristics)
                    .filter(([key, value]) => value)
                    .map(([key, value], i) => (
                      <div key={i} className="border-b border-gray-100 pb-3">
                        <p className="text-gray-500 capitalize">
                          {formatCharacteristic(key)}
                        </p>
                        <p className="font-medium">{value}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {activeTab === "location" && (
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Ubicación</h2>
                <div className="h-96 rounded-lg overflow-hidden">
                  <Map src={property.maps} />
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium mb-2">Dirección exacta</h3>
                  <p className="text-gray-700">
                    {property.address}, {property.country}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
